import { MAX_FAKTOR, DIVISION_AB_FACH } from '../config'
import type { Karte, KartenId, Variante, Zufall } from './typen'

/**
 * Handgeschriebene Merksaetze fuer die 21 schweren Karten: beide Faktoren
 * zwischen 3 und 9, ohne die 5er-Reihe. Genau die, an denen Kinder haengen.
 *
 * Jeder Satz geht ueber eine Kernaufgabe (1er, 2er, 5er, 10er) und nutzt
 * Verdoppeln, Halbieren oder Zerlegen. Das entspricht dem Vorgehen, das im
 * Unterricht gelehrt wird, damit die App nicht gegen die Schule arbeitet.
 */
const MERKSAETZE: Record<KartenId, string> = {
  '3x3': 'Denk an 3 + 3 + 3. Das sind 9.',
  '3x4': 'Denk an 3 × 2 = 6, und das doppelt: 6 + 6 = 12.',
  '3x6': 'Denk an 3 × 5 = 15, plus noch eine 3: 15 + 3 = 18.',
  '3x7': 'Denk an 3 × 5 = 15, plus 3 × 2 = 6. Zusammen 21.',
  '3x8': 'Denk an 3 × 4 = 12, und das doppelt: 12 + 12 = 24.',
  '3x9': 'Denk an 3 × 10 = 30, minus eine 3: 30 − 3 = 27.',
  '4x4': 'Denk an 4 × 2 = 8, und das doppelt: 8 + 8 = 16.',
  '4x6': 'Denk an 4 × 5 = 20, plus noch eine 4: 20 + 4 = 24.',
  '4x7': 'Denk an 4 × 5 = 20, plus 4 × 2 = 8. Zusammen 28.',
  '4x8': 'Denk an 4 × 4 = 16, und das doppelt: 16 + 16 = 32.',
  '4x9': 'Denk an 4 × 10 = 40, minus eine 4: 40 − 4 = 36.',
  '6x6': 'Denk an 6 × 5 = 30, plus noch eine 6: 30 + 6 = 36.',
  '6x7': 'Denk an 6 × 5 = 30, plus 6 × 2 = 12. Zusammen 42.',
  '6x8': 'Denk an 6 × 4 = 24, und das doppelt: 24 + 24 = 48.',
  '6x9': 'Denk an 6 × 10 = 60, minus eine 6: 60 − 6 = 54.',
  '7x7': 'Denk an 7 × 5 = 35, plus 7 × 2 = 14. Zusammen 49.',
  '7x8': 'Denk an 7 × 4 = 28, und das doppelt: 28 + 28 = 56.',
  '7x9': 'Denk an 7 × 10 = 70, minus eine 7: 70 − 7 = 63.',
  '8x8': 'Denk an 8 × 4 = 32, und das doppelt: 32 + 32 = 64.',
  '8x9': 'Denk an 8 × 10 = 80, minus eine 8: 80 − 8 = 72.',
  '9x9': 'Denk an 9 × 10 = 90, minus eine 9: 90 − 9 = 81.',
}

/**
 * Merksatz fuer alle uebrigen Karten, also alle mit mindestens einem Faktor
 * aus 1, 2, 5 oder 10. Das sind die Kernaufgaben selbst, sie brauchen keinen
 * Umweg, sondern nur die Regel benannt.
 */
function erzeugterMerksatz(a: number, b: number): string {
  const regel = (faktor: number, anderer: number): string | null => {
    switch (faktor) {
      case 1:
        return 'Mal 1 bleibt alles, wie es ist.'
      case 10:
        return `Bei mal 10 hängst du eine Null an: aus ${anderer} wird ${anderer * 10}.`
      case 5:
        return `Nimm mal 10 und halbiere: die Hälfte von ${anderer * 10} ist ${anderer * 5}.`
      case 2:
        return `Verdoppeln: ${anderer} + ${anderer} = ${anderer * 2}.`
      default:
        return null
    }
  }

  // Reihenfolge ist Absicht: 1 vor 10 vor 5 vor 2, von der einfachsten Regel
  // zur aufwendigsten. Bei 2 × 10 soll die Null-Regel greifen, nicht das
  // Verdoppeln von 10.
  for (const faktor of [1, 10, 5, 2]) {
    if (a === faktor) {
      const satz = regel(faktor, b)
      if (satz) return satz
    }
    if (b === faktor) {
      const satz = regel(faktor, a)
      if (satz) return satz
    }
  }

  // Kann nur eintreten, wenn MERKSAETZE unvollstaendig ist. Der Test
  // "jede Karte hat einen nicht leeren Merksatz" faengt das ab.
  return `Zerlege: ${a} × 5 = ${a * 5}, plus ${a} × ${b - 5} = ${a * (b - 5)}.`
}

/** Normalisierte Kennung, kleinerer Faktor zuerst. 6x7 und 7x6 werden gleich. */
export function kartenId(a: number, b: number): KartenId {
  const klein = Math.min(a, b)
  const gross = Math.max(a, b)
  return `${klein}x${gross}`
}

function baueKarte(a: number, b: number): Karte {
  const id = kartenId(a, b)
  return {
    id,
    a: Math.min(a, b),
    b: Math.max(a, b),
    produkt: a * b,
    merksatz: MERKSAETZE[id] ?? erzeugterMerksatz(Math.min(a, b), Math.max(a, b)),
  }
}

const KARTEN: Karte[] = (() => {
  const liste: Karte[] = []
  for (let a = 1; a <= MAX_FAKTOR; a++) {
    for (let b = a; b <= MAX_FAKTOR; b++) {
      liste.push(baueKarte(a, b))
    }
  }
  return liste
})()

const KARTEN_NACH_ID = new Map(KARTEN.map((k) => [k.id, k]))

export function alleKarten(): Karte[] {
  return KARTEN
}

export function karteFuer(id: KartenId): Karte {
  const karte = KARTEN_NACH_ID.get(id)
  if (!karte) throw new Error(`Unbekannte Karte: ${id}`)
  return karte
}

/**
 * Die Aufgabenvarianten einer Karte.
 *
 * Eine gemischte Karte wie {6,7} hat vier: 6 × 7, 7 × 6, 42 : 6, 42 : 7.
 * Eine Quadratkarte hat nur zwei, weil 7 × 7 und 49 : 7 jeweils nur einmal
 * existieren. Karten mit Faktor 1 bekommen gar keine Division: 7 : 1 und
 * 7 : 7 sind als Uebung wertlos.
 */
export function variantenFuer(karte: Karte): Variante[] {
  const { a, b, produkt, id } = karte
  const varianten: Variante[] = [
    {
      id: `${a}x${b}`,
      karte: id,
      typ: 'mal',
      links: a,
      rechts: b,
      ergebnis: produkt,
      text: `${a} × ${b}`,
    },
  ]

  if (a !== b) {
    varianten.push({
      id: `${b}x${a}`,
      karte: id,
      typ: 'mal',
      links: b,
      rechts: a,
      ergebnis: produkt,
      text: `${b} × ${a}`,
    })
  }

  if (a >= 2) {
    varianten.push({
      id: `${produkt}:${b}`,
      karte: id,
      typ: 'geteilt',
      links: produkt,
      rechts: b,
      ergebnis: a,
      text: `${produkt} : ${b}`,
    })

    if (a !== b) {
      varianten.push({
        id: `${produkt}:${a}`,
        karte: id,
        typ: 'geteilt',
        links: produkt,
        rechts: a,
        ergebnis: b,
        text: `${produkt} : ${a}`,
      })
    }
  }

  return varianten
}

/**
 * Welche Varianten in diesem Fach gestellt werden duerfen.
 *
 * Division baut auf der gesicherten Multiplikation auf. Eine Karte, die als
 * Malaufgabe noch hakt, wird nicht zusaetzlich als Divisionsaufgabe gestellt.
 */
export function erlaubteVarianten(karte: Karte, fach: number): Variante[] {
  const alle = variantenFuer(karte)
  if (fach < DIVISION_AB_FACH) {
    return alle.filter((v) => v.typ === 'mal')
  }
  return alle
}

function mischen<T>(liste: T[], zufall: Zufall): T[] {
  const kopie = [...liste]
  for (let i = kopie.length - 1; i > 0; i--) {
    const j = Math.floor(zufall() * (i + 1))
    const a = kopie[i]
    const b = kopie[j]
    if (a !== undefined && b !== undefined) {
      kopie[i] = b
      kopie[j] = a
    }
  }
  return kopie
}

/** Nimmt aus den Stufen der Reihe nach, bis genug beisammen ist. */
function waehleAusStufen(
  stufen: number[][],
  anzahl: number,
  verboten: number,
  zufall: Zufall,
): number[] {
  const gewaehlt: number[] = []
  for (const stufe of stufen) {
    for (const kandidat of mischen(stufe, zufall)) {
      if (gewaehlt.length >= anzahl) return gewaehlt
      if (kandidat === verboten) continue
      if (gewaehlt.includes(kandidat)) continue
      gewaehlt.push(kandidat)
    }
  }
  return gewaehlt
}

const EINMALEINS_ERGEBNISSE: number[] = (() => {
  const menge = new Set<number>()
  for (let a = 1; a <= MAX_FAKTOR; a++) {
    for (let b = 1; b <= MAX_FAKTOR; b++) menge.add(a * b)
  }
  return [...menge].sort((x, y) => x - y)
})()

function ziffernTausch(zahl: number): number | null {
  if (zahl < 10 || zahl > 99) return null
  const zehner = Math.floor(zahl / 10)
  const einer = zahl % 10
  if (zehner === einer || einer === 0) return null
  return einer * 10 + zehner
}

/**
 * Die drei falschen Antworten fuer das Ballon-Format.
 *
 * Dieser Baustein entscheidet, ob das Ballon-Format etwas wert ist. Stuenden
 * neben 56 die Zahlen 12, 100 und 7, tippt das Kind nach zwei Tagen nur noch
 * die einzige plausible Zahl und lernt gar nichts. Deshalb kommen die
 * Distraktoren aus den Nachbarreihen, nie aus einem Zufallsgenerator ueber
 * den ganzen Zahlenraum.
 */
export function distraktoren(variante: Variante, zufall: Zufall): number[] {
  return variante.typ === 'mal'
    ? distraktorenMal(variante, zufall)
    : distraktorenGeteilt(variante, zufall)
}

function distraktorenMal(variante: Variante, zufall: Zufall): number[] {
  const p = variante.ergebnis
  const a = variante.links
  const b = variante.rechts

  // Plausibilitaetsgrenze: Ein Distraktor, der weit weg liegt, verraet sich
  // selbst. Die Pruefung gehoert in "gueltig" und damit VOR die Auswahl, nicht
  // hinter sie. Sonst belegt etwa der Zifferndreher von 48 (naemlich 84) einen
  // der drei Plaetze und faellt danach weg, und es bleiben nur zwei Ballons.
  //
  // Die Spanne waechst mit dem Ergebnis, aber gedeckelt: Bei 2 x 2 = 4 ist
  // eine 10 daneben schon zu offensichtlich falsch, bei 7 x 8 = 56 ist eine
  // 48 dagegen genau richtig verlockend.
  const grenze = Math.max(4, Math.min(20, Math.ceil(p * 0.5)))
  const gueltig = (x: number) =>
    x >= 1 && x <= 100 && x !== p && Math.abs(x - p) <= grenze

  // Stufe 1: die direkten Nachbarn im Einmaleins. 7 x 8 = 56 liefert
  // 49, 63 (7er-Reihe) sowie 48, 64 (8er-Reihe).
  const nachbarn = [a * (b - 1), a * (b + 1), (a - 1) * b, (a + 1) * b].filter(gueltig)

  // Stufe 2: Zifferndreher. 56 wird zu 65. Ein echter Fluechtigkeitsfehler.
  const dreher = [ziffernTausch(p)].filter((x): x is number => x !== null && gueltig(x))

  // Stufe 3: um einen Faktor daneben. Klassischer Verzaehler beim Zusammenzaehlen.
  const verzaehlt = [p + a, p - a, p + b, p - b].filter(gueltig)

  // Stufe 4: irgendein anderes Einmaleins-Ergebnis in der Naehe.
  const nahe = EINMALEINS_ERGEBNISSE.filter(gueltig)

  // Stufe 5: Notnagel, falls oben alles leer bleibt. Kann bei winzigen
  // Ergebnissen vorkommen, wo es schlicht wenige Nachbarn gibt.
  const notnagel: number[] = []
  for (let d = 1; d <= grenze; d++) {
    if (gueltig(p + d)) notnagel.push(p + d)
    if (gueltig(p - d)) notnagel.push(p - d)
  }

  return waehleAusStufen([nachbarn, dreher, verzaehlt, nahe, notnagel], 3, p, zufall)
}

function distraktorenGeteilt(variante: Variante, zufall: Zufall): number[] {
  // Bei Geteiltaufgaben liegt das Ergebnis immer zwischen 1 und 10. Alles
  // ausserhalb waere sofort als Unsinn erkennbar und damit geschenkt.
  const e = variante.ergebnis
  const gueltig = (x: number) => x >= 1 && x <= MAX_FAKTOR && x !== e

  const dicht = [e - 1, e + 1].filter(gueltig)
  const weiter = [e - 2, e + 2].filter(gueltig)
  const rest = Array.from({ length: MAX_FAKTOR }, (_, i) => i + 1).filter(gueltig)

  return waehleAusStufen([dicht, weiter, rest], 3, e, zufall)
}

/** Die vier Ballons: richtige Loesung plus drei Distraktoren, gemischt. */
export function ballonAntworten(variante: Variante, zufall: Zufall): number[] {
  return mischen([variante.ergebnis, ...distraktoren(variante, zufall)], zufall)
}
